import React, { useEffect, useRef, useState } from "react";
import { useMemo } from "react";
import { AlertTriangle, Clock, QrCode } from "lucide-react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { CapacitorBarcodeScanner, CapacitorBarcodeScannerTypeHint } from "@capacitor/barcode-scanner";
import { Button } from "@/components/ui/button";
import {
  usePatrolConfig,
  usePatrolOutboxRecords,
  usePatrolScanSubmit,
  usePatrolStatus,
} from "../hooks";
import { useOpenShift } from "@/features/shifts/hooks";
import { countUniquePendingInWindow } from "../offline/patrolScanOutbox";
import { normalizePatrolWindow } from "../utils/normalizePatrolWindow";
import { resolvePatrolDisplayPeriod } from "../utils/patrolDisplayPeriod";
import {
  getPatrolImminentThresholdMs,
  resolvePatrolEffectiveWindow,
} from "../utils/resolvePatrolEffectiveWindow";
import { openFilePickerForQr } from "../utils/decodeQr";
import { formatTimeInZone } from "@/features/shifts/utils/formatShiftTime";
import { useToast } from "@/hooks/use-toast";

interface PatrolScanCardProps {
  /**
   * When true the card is only rendered when there is an active open shift.
   * Defaults to false (matches original ShiftsPage behaviour: show whenever
   * patrol is configured, open-shift or not).
   */
  requireOpenShift?: boolean;
  className?: string;
}

/**
 * Self-contained patrol scan card. Calls all patrol hooks internally.
 *
 * Set `requireOpenShift` to `true` on pages where patrol scanning only makes
 * sense while a shift is active (e.g. ScheduledShiftsPage).
 */
export function PatrolScanCard({ requireOpenShift = false, className }: PatrolScanCardProps) {
  const { data: patrolConfig } = usePatrolConfig();
  const { data: patrolStatus } = usePatrolStatus();
  const { data: patrolOutbox = [] } = usePatrolOutboxRecords();
  const { data: openShift } = useOpenShift();
  const { submitPayload, isPending: isPatrolSubmitPending } = usePatrolScanSubmit();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [isPatrolScanning, setIsPatrolScanning] = useState(false);

  const showCard = patrolConfig != null && (!requireOpenShift || openShift != null);

  const patrolRequiredScans = patrolConfig?.min_qr_codes_to_scan ?? 0;
  const patrolStatusRequiredScans = patrolStatus?.min_qr_codes_to_scan ?? patrolRequiredScans;
  const patrolScannedInPeriod = patrolStatus?.unique_scanned_in_period ?? 0;

  const patrolPeriod = useMemo(() => normalizePatrolWindow(patrolStatus), [patrolStatus]);
  const patrolPeriodEndIso = patrolPeriod?.periodEndIso ?? patrolStatus?.window_end ?? "";
  const patrolDisplayPeriod = useMemo(
    () =>
      resolvePatrolDisplayPeriod({
        normalized: patrolPeriod,
        openShift: openShift ?? undefined,
        periodAnchorIso: patrolStatus?.period_started_at,
        fallbackIntervalMinutes: patrolConfig?.patrol_interval_minutes ?? null,
      }),
    [patrolPeriod, openShift, patrolStatus?.period_started_at, patrolConfig?.patrol_interval_minutes]
  );
  const patrolEffective = useMemo(
    () =>
      resolvePatrolEffectiveWindow({
        normalized: patrolPeriod,
        openShift: openShift ?? null,
        intervalMinutes: patrolConfig?.patrol_interval_minutes ?? null,
        resolvedDisplay: patrolDisplayPeriod,
        fallbackStartIso: patrolPeriod?.periodStartIso ?? patrolStatus?.window_start ?? "",
        fallbackDeadlineIso: patrolPeriodEndIso,
      }),
    [
      patrolPeriod,
      openShift,
      patrolConfig?.patrol_interval_minutes,
      patrolDisplayPeriod,
      patrolPeriodEndIso,
      patrolStatus?.window_start,
    ]
  );

  const patrolTz = openShift?.timezone ?? "UTC";
  const patrolDisplayStartIso = patrolEffective.displayStartIso;
  const patrolDisplayEndIso = patrolEffective.displayEndIso;

  const pendingUniqueInDisplayWindow = useMemo(() => {
    if (!patrolDisplayStartIso || !patrolDisplayEndIso) return 0;
    return countUniquePendingInWindow(patrolOutbox, patrolDisplayStartIso, patrolDisplayEndIso);
  }, [patrolOutbox, patrolDisplayStartIso, patrolDisplayEndIso]);

  const patrolEffectiveScanned = patrolScannedInPeriod + pendingUniqueInDisplayWindow;
  const patrolProgressPercent =
    patrolStatusRequiredScans > 0
      ? Math.min(100, (100 * patrolEffectiveScanned) / patrolStatusRequiredScans)
      : 0;

  const patrolWindowEnd =
    patrolEffective.deadlineIso !== "" ? new Date(patrolEffective.deadlineIso) : null;
  const patrolRemainingMs =
    patrolWindowEnd != null && !Number.isNaN(patrolWindowEnd.getTime())
      ? patrolWindowEnd.getTime() - Date.now()
      : null;
  const patrolIsComplete =
    patrolStatus != null && patrolStatus.min_qr_codes_to_scan > 0
      ? patrolEffectiveScanned >= patrolStatus.min_qr_codes_to_scan
      : false;

  const isPatrolOverdue =
    openShift != null &&
    patrolStatus != null &&
    !patrolIsComplete &&
    patrolRemainingMs != null &&
    patrolRemainingMs < 0;

  const patrolImminentThresholdMs = getPatrolImminentThresholdMs(
    patrolConfig?.patrol_interval_minutes ?? 0
  );
  const isPatrolImminent =
    openShift != null &&
    patrolStatus != null &&
    !patrolIsComplete &&
    patrolRemainingMs != null &&
    patrolRemainingMs >= 0 &&
    patrolImminentThresholdMs != null &&
    patrolRemainingMs <= patrolImminentThresholdMs;

  const handlePatrolScanQr = async () => {
    if (isPatrolScanning || isPatrolSubmitPending) return;
    setIsPatrolScanning(true);
    try {
      const win = typeof window !== "undefined"
        ? (window as Window & { __E2E_PATROL_SCAN_PAYLOAD?: string })
        : undefined;
      const e2ePayload = win?.__E2E_PATROL_SCAN_PAYLOAD;
      if (e2ePayload != null) {
        if (win) delete win.__E2E_PATROL_SCAN_PAYLOAD;
        await submitPayload(e2ePayload.trim());
        return;
      }
      const result = await CapacitorBarcodeScanner.scanBarcode({
        hint: CapacitorBarcodeScannerTypeHint.QR_CODE,
        scanInstructions: "Position the QR code within the frame",
      });
      const payload = result?.ScanResult?.trim();
      if (payload) {
        await submitPayload(payload);
      } else {
        toast({
          title: "No QR code found",
          description: "Try again or choose an image from your gallery.",
          variant: "destructive",
        });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Scan was cancelled or failed";
      if (!message.toLowerCase().includes("cancel")) {
        toast({
          title: "Scan failed",
          description: message,
          variant: "destructive",
        });
      }
    } finally {
      setIsPatrolScanning(false);
    }
  };

  const handlePatrolChooseGallery = async () => {
    if (isPatrolSubmitPending) return;
    const payload = await openFilePickerForQr();
    if (payload) {
      await submitPayload(payload);
    } else {
      toast({
        title: "No QR code found",
        description: "The image didn't contain a valid QR code.",
        variant: "destructive",
      });
    }
  };

  // Auto-open scanner when navigated here with openPatrolScanner flag
  const didAutoOpenScannerRef = useRef(false);
  const openPatrolScannerFromState =
    (location.state as { openPatrolScanner?: boolean } | null)?.openPatrolScanner === true;
  const openPatrolScannerFromQuery = searchParams.get("openPatrolScanner") === "1";
  const openPatrolScannerRequested = openPatrolScannerFromState || openPatrolScannerFromQuery;

  useEffect(() => {
    if (!openPatrolScannerRequested) return;
    if (didAutoOpenScannerRef.current) return;
    if (!showCard) return;
    didAutoOpenScannerRef.current = true;
    navigate({ pathname: location.pathname, search: "" }, { replace: true, state: null });
    void handlePatrolScanQr();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openPatrolScannerRequested, showCard, location.pathname, navigate, searchParams]);

  if (!showCard || !patrolConfig) return null;

  return (
    <section id="qr-scan-section" className={className} data-testid="patrol-scan-card">
      {patrolStatus && isPatrolOverdue ? (
        <div className="bg-red-600 rounded-2xl shadow-lg border-2 border-red-700 p-5">
          <div className="flex justify-between items-start mb-3">
            <h3 className="font-bold text-white text-lg flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-300" />
              Next Scan Due
            </h3>
            <span className="bg-white text-red-600 text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wide">
              Critical
            </span>
          </div>
          <p className="text-white text-base font-bold mb-4 flex items-center gap-2">
            <Clock className="h-4 w-4 text-yellow-300" />
            OVERDUE
          </p>
          <div className="space-y-3 mb-5">
            <div className="bg-red-700/50 backdrop-blur-sm rounded-lg p-3 border border-red-500/30">
              <p className="text-xs text-red-200 mb-1">Expected period</p>
              <p className="text-sm font-semibold text-white">
                {formatTimeInZone(patrolDisplayStartIso, patrolTz)} –{" "}
                {formatTimeInZone(patrolDisplayEndIso, patrolTz)}
              </p>
              <p className="text-xs text-red-200/90 mt-2 leading-snug">
                Patrol rule: {patrolStatusRequiredScans} scans every{" "}
                {patrolConfig.patrol_interval_minutes} min.
              </p>
            </div>
            <div>
              <div className="flex justify-between text-xs mb-1.5">
                <span className="text-red-100">Patrol Progress</span>
                <span className="text-white font-bold">
                  {patrolEffectiveScanned} of {patrolStatusRequiredScans} scanned
                </span>
              </div>
              <div className="w-full bg-red-900/50 rounded-full h-2.5 border border-red-500/30">
                <div
                  className="bg-yellow-400 h-2.5 rounded-full transition-all duration-500"
                  style={{ width: `${patrolProgressPercent}%` }}
                  role="progressbar"
                  aria-valuenow={patrolEffectiveScanned}
                  aria-valuemin={0}
                  aria-valuemax={patrolStatusRequiredScans}
                />
              </div>
            </div>
          </div>
          <Button
            onClick={handlePatrolScanQr}
            disabled={isPatrolSubmitPending || isPatrolScanning}
            className="w-full bg-white text-red-600 font-bold py-4 rounded-lg shadow-xl hover:bg-red-50 active:scale-[0.98] transition-all flex items-center justify-center gap-3 text-base"
          >
            <QrCode className="h-5 w-5" />
            {isPatrolScanning ? "Opening scanner…" : "SCAN QR CODE"}
          </Button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <div className="flex justify-between items-start mb-3">
            <h3 className="font-bold text-gray-800 text-lg">Next Scan Due</h3>
            {isPatrolImminent ? (
              <span className="bg-orange-100 text-orange-600 text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wide">
                Warning
              </span>
            ) : (
              <span className="bg-red-50 text-red-500 text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wide">
                Priority
              </span>
            )}
          </div>
          {patrolStatus ? (
            <p
              className={
                isPatrolImminent
                  ? "text-orange-600 text-sm font-semibold mb-4"
                  : "text-gray-500 text-sm mb-4"
              }
            >
              {isPatrolImminent
                ? "Due imminently"
                : `You must scan ${patrolStatus.min_qr_codes_to_scan} QR codes before ${formatTimeInZone(
                    patrolDisplayEndIso,
                    patrolTz
                  )}`}
            </p>
          ) : (
            <p className="text-gray-500 text-sm mb-4">
              Scan {patrolConfig.min_qr_codes_to_scan} QR codes every{" "}
              {patrolConfig.patrol_interval_minutes} minutes
            </p>
          )}
          <div className="space-y-3 mb-5">
            {patrolStatus && (
              <div>
                <p className="text-xs text-gray-400 mb-1">Current period</p>
                <p className="text-sm font-medium text-gray-700">
                  {formatTimeInZone(patrolDisplayStartIso, patrolTz)} –{" "}
                  {formatTimeInZone(patrolDisplayEndIso, patrolTz)}
                </p>
                <p className="text-xs text-gray-400 mt-2 leading-snug">
                  Patrol rule: {patrolStatusRequiredScans || patrolRequiredScans} scans every{" "}
                  {patrolConfig.patrol_interval_minutes} min.
                </p>
              </div>
            )}
            <div>
              <div className="flex justify-between text-xs mb-1.5">
                <span className="text-gray-500">Patrol Progress</span>
                <span
                  className={
                    isPatrolImminent ? "text-orange-600 font-medium" : "text-blue-600 font-medium"
                  }
                >
                  {patrolEffectiveScanned} of {patrolStatusRequiredScans || patrolRequiredScans} scanned
                </span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2">
                <div
                  className={
                    isPatrolImminent
                      ? "bg-orange-500 h-2 rounded-full transition-all duration-500"
                      : "bg-blue-500 h-2 rounded-full transition-all duration-500"
                  }
                  style={{ width: `${patrolProgressPercent}%` }}
                  role="progressbar"
                  aria-valuenow={patrolEffectiveScanned}
                  aria-valuemin={0}
                  aria-valuemax={patrolStatusRequiredScans || patrolRequiredScans}
                />
              </div>
            </div>
          </div>
          <Button
            onClick={handlePatrolScanQr}
            disabled={isPatrolSubmitPending || isPatrolScanning}
            className={
              isPatrolImminent
                ? "w-full bg-orange-500 text-white font-medium py-3.5 rounded-lg shadow-md hover:bg-orange-600 active:scale-[0.98] transition-all flex items-center justify-center gap-2"
                : "w-full bg-gray-900 text-white font-medium py-3.5 rounded-lg shadow-md hover:bg-gray-800 active:scale-[0.98] transition-all flex items-center justify-center gap-2"
            }
          >
            <QrCode className="h-5 w-5" />
            {isPatrolScanning ? "Opening scanner…" : "Scan QR code"}
          </Button>
        </div>
      )}
    </section>
  );
}
